import { PAYMENTS_ENABLED } from "@/lib/config";
import { REGISTER_PATH } from "@/lib/routes";
import { STUDIO_URL } from "@/lib/studio";

/**
 * La navigation du site, définie une seule fois (RDEC-019).
 *
 * Les deux en-têtes (accueil, pages internes) et les deux pieds de page lisent
 * ces listes ; chacun garde son apparence, aucun ne tient sa propre liste. Les
 * libellés sont des clés de traduction, pour que les listes restent des données
 * testables sans rendu.
 *
 * Le compte n'y figure pas : « Connexion », « Mon compte » et l'espace
 * professionnel vivent dans le bloc de compte (`AuthControl`), une seule fois.
 *
 * Un lien `external` sort du site (Provena Studio, RDEC-023) : il est rendu
 * en simple ancre, jamais par le routeur.
 *
 * @typedef {{ to: string, label: string, auth?: boolean, external?: boolean, id?: string }} NavLink
 */

/**
 * La barre : les entrées du domaine. Tarifs n'y entre que lorsque les
 * paiements existent ; d'ici là, la page reste au pied de page.
 *
 * @param {boolean} [paymentsEnabled]
 * @returns {NavLink[]}
 */
export const barLinks = (paymentsEnabled = PAYMENTS_ENABLED) => [
  { to: "/verifier", label: "header.nav.verify" },
  { to: "/montres-volees", label: "header.nav.stolen" },
  { to: "/le-passeport", label: "header.nav.passport" },
  { to: "/comment-ca-marche", label: "header.nav.howItWorks" },
  ...(paymentsEnabled ? [{ to: "/tarifs", label: "footer.pricing" }] : []),
];

/** L'action principale du site, à droite des entrées. */
export const REGISTER_ACTION = { to: REGISTER_PATH, label: "header.register" };

/** Ce que le menu ajoute à la barre quand elle se replie. */
export const MENU_EXTRA_LINKS = [
  { to: "/galerie", label: "header.nav.gallery" },
  { to: "/a-propos", label: "footer.about" },
];

/**
 * Le pied de page : toutes les destinations, en quatre groupes. `auth` fait
 * passer par la connexion un visiteur qui n'en a pas.
 *
 * @type {{ id: string, title: string, links: NavLink[] }[]}
 */
export const FOOTER_GROUPS = [
  {
    id: "registre",
    title: "footer.groups.registry",
    links: [
      { to: "/verifier", label: "header.nav.verify" },
      { to: "/montres-volees", label: "header.nav.stolen" },
      { to: "/declarer-vol", label: "footer.reportTheft" },
      { to: "/signaler-vol-montre", label: "footer.stolenGuide" },
    ],
  },
  {
    id: "comprendre",
    title: "footer.groups.understand",
    links: [
      { to: "/le-passeport", label: "header.nav.passport" },
      { to: "/comment-ca-marche", label: "header.nav.howItWorks" },
      { to: "/faq", label: "footer.faq" },
      { to: "/galerie", label: "header.nav.gallery" },
      { to: "/signataires", label: "signatory.title" },
      { to: "/a-propos", label: "footer.about" },
      { to: "/tarifs", label: "footer.pricing" },
    ],
  },
  {
    id: "compte",
    title: "footer.groups.account",
    links: [
      { to: REGISTER_PATH, label: "footer.register" },
      { to: "/mon-compte", label: "footer.account", auth: true },
      { to: "/professionnel", label: "header.professionals" },
      // RDEC-023 : l'outil de travail des ateliers, sur toutes les pages.
      { to: STUDIO_URL, label: "footer.studio", external: true, id: "studio" },
    ],
  },
  {
    id: "cadre",
    title: "footer.groups.legal",
    links: [
      { to: "/vos-donnees", label: "footer.yourData" },
      { to: "/politique-confidentialite", label: "footer.privacy" },
      { to: "/politique-cookies", label: "footer.cookies" },
      { to: "/conditions", label: "footer.terms" },
    ],
  },
];

/** Adresse effective d'un lien : un lien `auth` passe par la connexion. */
export const navTarget = (link, user) =>
  link.auth && !user ? `/login?redirect=${encodeURIComponent(link.to)}` : link.to;

/** Identifiant de test stable, dérivé de l'adresse. */
export const navTestId = (prefix, link) =>
  `${prefix}-${link.id || link.to.replace(/^\//, "") || "home"}`;
